import { signOut } from 'firebase/auth';
import firebaseAuth from '~/config/firebase/auth';
import { olvidarDirectorios } from '~/pages/public/Forms/Reservation/components/directorioClientes';

/**
 * Borra lo que la aplicación haya dejado guardado en el navegador: datos de
 * sesión, preferencias y archivos cacheados. Así la próxima vez que se entre
 * se carga la última versión publicada, sin tener que recargar a mano.
 *
 * Cada bloque va por separado porque en ventanas privadas o con las cookies
 * bloqueadas cualquiera de ellos puede lanzar una excepción, y un fallo no
 * debe impedir que se limpien los demás ni que se cierre la sesión.
 */
export const clearBrowserData = async (): Promise<void> => {
  try {
    localStorage.clear();
  } catch {
    /* el navegador puede tener el almacenamiento bloqueado */
  }

  try {
    sessionStorage.clear();
  } catch {
    /* idem */
  }

  try {
    if (typeof caches !== 'undefined') {
      const keys = await caches.keys();
      await Promise.all(keys.map((key) => caches.delete(key)));
    }
  } catch {
    /* la Cache API no está disponible en todos los contextos */
  }
};

/**
 * Recarga la aplicación descartando los archivos cacheados, pero **sin tocar
 * la sesión**: se usa cuando hay una versión nueva publicada y la persona
 * decide actualizar. A diferencia de `clearBrowserData`, no vacía
 * localStorage, así que no se cierra la sesión ni se pierden preferencias.
 */
export const reloadWithFreshAssets = async (): Promise<void> => {
  try {
    if (typeof caches !== 'undefined') {
      const keys = await caches.keys();
      await Promise.all(keys.map((key) => caches.delete(key)));
    }
  } catch {
    /* la Cache API no está disponible en todos los contextos */
  }

  window.location.reload();
};

/**
 * Cierra la sesión y recarga la aplicación desde cero.
 *
 * Se usa `location.replace` en lugar de navegar con el router para que el
 * navegador vuelva a pedir el index.html y no quede nada del estado anterior
 * en memoria. También evita que el botón "atrás" devuelva a una pantalla
 * privada ya cerrada.
 */
export const signOutAndClear = async (redirectTo = '/login'): Promise<void> => {
  // Antes de cerrar: soltar el directorio de clientes. Sin esto, las
  // suscripciones abiertas se quedan pidiendo datos que la sesión recién
  // cerrada ya no puede leer, y la libreta de 2.455 personas seguiría en
  // memoria de la pestaña hasta que el navegador recargue.
  olvidarDirectorios();

  try {
    await signOut(firebaseAuth);
  } catch {
    /* si ya no había sesión, seguimos limpiando igual */
  }

  await clearBrowserData();
  window.location.replace(redirectTo);
};
